/**
 * Google-Anmeldung im Browser (V-2549/V-2551) — gemeinsamer Teil für BIS ERP, WebPOS, Kundencenter und BIS Archiv.
 *
 * Ablauf:
 *  1. `starteGoogleAnmeldung()` erzeugt einen PKCE-Verifier (nur in diesem Tab, `sessionStorage`), schickt dessen
 *     SHA-256-Challenge an den Start-Endpunkt und leitet zu Google.
 *  2. Der Rücksprung trägt KEIN Token, sondern einen Einmal-Code im Fragment (`#google_auth=success&google_code=…`).
 *     `leseGoogleRueckgabe()` liest ihn und räumt die Adresszeile SOFORT (synchron, vor jedem `await`) auf.
 *  3. `loeseGoogleCodeEin()` tauscht Code + Verifier gegen das Token (POST). Ein Code, der in einem anderen Tab oder
 *     Browser gestartet wurde (untergeschobener Link, Login-CSRF), ist ohne den Verifier dieses Tabs wertlos.
 *
 * 🚨 Früher stand das Token als `?token=` in der Adresse (Verlauf, Referer, Protokolle, Matomo) und jede Seite übernahm
 * JEDES Token aus der Adresse — ein präparierter Link meldete das Opfer im Konto des Angreifers an. Dieser Lesepfad ist
 * ersatzlos entfallen; neue Seiten dürfen kein `searchParams.get('token')` für die Google-Anmeldung lesen.
 */

const SPEICHER_KEY = 'bis_google_pkce';
// Mindestens so lang wie die Server-Sitzung (PHP gc_maxlifetime ~24 min): wer bei Google länger braucht (2FA,
// Kontowiederherstellung), kommt erfolgreich zurück und soll nicht „anderes Fenster" lesen. Die Bindung leistet die
// Challenge, der Code selbst lebt nur 60 s.
const MAX_ALTER_MS = 30 * 60 * 1000;

/** Parameter, die der Rücksprung trägt (in Query oder Fragment) — beim Aufräumen der Adresszeile entfernt. */
const GOOGLE_PARAMS = ['google_auth', 'google_code', 'new_user', 'fehler', 'message', 'tenants', 'token', 'accessToken', 'refreshToken'];

/**
 * F-007/F-011: Die ERP-Anmeldeseite und die Registrierung laufen auch auf pos.* und admin.*. Der PKCE-Verifier liegt im
 * `sessionStorage` des startenden Hosts, der Rücksprung geht aber fest auf den ERP-Host — ein Start dort endete in einer
 * Sackgasse. Diese Hilfen erkennen den Fall und bilden den Schwester-Host `erp.*` aus dem aktuellen Origin ab (wie
 * `erpOrigin()` im Kundencenter, nichts hart kodiert).
 */
export function istFremderErpHost(): boolean {
  return /^(pos|admin)\./i.test(window.location.hostname);
}

/** Adresse `pfad` (mit führendem `/`, optional mit Query) auf dem Schwester-Host `erp.*`. */
export function erpSchwesterUrl(pfad: string): string {
  return `${window.location.origin.replace(/^(https?:\/\/)(pos|admin)\./i, '$1erp.')}${pfad}`;
}

/** Fehlerschlüssel des Servers → feste i18n-Texte (F-002: nie Freitext aus der Adresse anzeigen). */
const FEHLER_TEXTE: Record<string, string> = {
  abgebrochen: 'auth.googleFehlerAbgebrochen',
  sitzung_abgelaufen: 'auth.googleFehlerSitzungAbgelaufen',
  seite_neu_laden: 'auth.googleFehlerSeiteNeuLaden',
  konto_gesperrt: 'auth.googleFehlerKontoGesperrt',
  kein_betrieb: 'auth.googleFehlerKeinBetrieb',
  nicht_aktiviert: 'auth.googleFehlerNichtAktiviert',
};

/** Anzeigetext zu einem Fehlerschlüssel des Rücksprungs; unbekannt/fehlend → allgemeiner Text. */
export function googleFehlerText(fehler: string | null | undefined, t: (key: string) => string): string {
  const key = fehler && Object.prototype.hasOwnProperty.call(FEHLER_TEXTE, fehler) ? FEHLER_TEXTE[fehler] : 'auth.googleLoginFailed';
  return t(key);
}

function base64url(bytes: Uint8Array): string {
  let text = '';
  bytes.forEach((b) => {
    text += String.fromCharCode(b);
  });
  return btoa(text).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Startet die Google-Anmeldung: Verifier erzeugen und ablegen, Challenge anhängen, zu `startUrl` (Server) navigieren.
 * Gibt `false` zurück, wenn der Browser das nicht kann (kein `crypto.subtle`, `sessionStorage` gesperrt) — dann bleibt
 * die Seite stehen und der Aufrufer zeigt eine Fehlermeldung.
 */
export async function starteGoogleAnmeldung(startUrl: string, params: Record<string, string | undefined> = {}): Promise<boolean> {
  try {
    const verifier = base64url(crypto.getRandomValues(new Uint8Array(32)));
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    const challenge = base64url(new Uint8Array(digest));
    sessionStorage.setItem(SPEICHER_KEY, JSON.stringify({ verifier, ts: Date.now() }));

    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v) query.set(k, v);
    });
    query.set('code_challenge', challenge);
    query.set('code_challenge_method', 'S256');
    window.location.href = `${startUrl}?${query.toString()}`;
    return true;
  } catch {
    return false;
  }
}

export interface GoogleRueckgabe {
  status: 'success' | 'error' | 'select' | 'business_terms_required';
  /** Einmal-Code (nur bei `success`). */
  code: string | null;
  /** Fehlerschlüssel des Servers (nur bei `error`), anzeigen über `googleFehlerText()`. */
  fehler: string | null;
  neuerBenutzer: boolean;
  /** Mandantenliste (Base64-JSON, nur bei `select`). */
  mandanten: string | null;
}

/**
 * Liest die Rückgabe der Google-Anmeldung aus Fragment und Query und räumt die Adresszeile SOFORT auf (synchron — React
 * StrictMode ruft Effekte doppelt auf, ein zweiter Aufruf findet dann nichts mehr und löst den Code nicht doppelt ein).
 * Gibt `null` zurück, wenn die Adresse keine Google-Rückgabe trägt.
 */
export function leseGoogleRueckgabe(): GoogleRueckgabe | null {
  const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const query = new URLSearchParams(window.location.search);
  const quelle = fragment.get('google_auth') ? fragment : query.get('google_auth') ? query : null;
  if (!quelle) return null;

  const rohStatus = quelle.get('google_auth');
  const code = quelle.get('google_code');
  const rueckgabe: GoogleRueckgabe = {
    status: 'error',
    code: null,
    fehler: quelle.get('fehler'),
    neuerBenutzer: quelle.get('new_user') === '1',
    mandanten: quelle.get('tenants'),
  };
  if (rohStatus === 'success' && code) {
    rueckgabe.status = 'success';
    rueckgabe.code = code;
  } else if (rohStatus === 'select' || rohStatus === 'business_terms_required') {
    rueckgabe.status = rohStatus;
  }
  // `success` ohne Code (alter Link mit `?token=`) bleibt `error` ohne Schlüssel — das Token wird nie gelesen.

  GOOGLE_PARAMS.forEach((k) => query.delete(k));
  const rest = query.toString();
  window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : ''));

  // F-002: Eine Mandantenauswahl gehört nur zu einem Ablauf, den DIESER Tab gestartet hat (Verifier in sessionStorage);
  // sonst könnte jeder per Link eine erfundene Auswahl einschleusen. Ohne Verifier wird sie ignoriert.
  // F-012: statt still zu verschwinden, meldet er einen Fehler (`sitzung_abgelaufen`), damit jede Seite eine Meldung zeigt.
  if (rueckgabe.status === 'select' && holeVerifier() === null) {
    return { status: 'error', code: null, fehler: 'sitzung_abgelaufen', neuerBenutzer: false, mandanten: null };
  }

  return rueckgabe;
}

/** `ungueltig` = 400 (Code unbekannt/abgelaufen/verwendet), `abgelehnt` = 403, `netz` = Netz/5xx/Sonstiges. */
export type GoogleEinloeseFehler = 'anderes_fenster' | 'ungueltig' | 'abgelehnt' | 'netz';

export type GoogleEinloseErgebnis<T> =
  | { ok: true; daten: T }
  | { ok: false; art: GoogleEinloeseFehler; nachricht?: string };

function holeVerifier(): string | null {
  try {
    const roh = sessionStorage.getItem(SPEICHER_KEY);
    if (!roh) return null;
    const { verifier, ts } = JSON.parse(roh) as { verifier?: string; ts?: number };
    if (!verifier || typeof ts !== 'number' || Date.now() - ts > MAX_ALTER_MS) {
      sessionStorage.removeItem(SPEICHER_KEY);
      return null;
    }
    return verifier;
  } catch {
    return null;
  }
}

/**
 * Löst den Einmal-Code gegen das Token ein (POST `endpunkt` mit `{code, code_verifier}`). Der Verifier wird nach der
 * Serverantwort verworfen (bei einem reinen Netzwerkfehler bleibt er, der Code ist dann noch unverbraucht).
 */
export async function loeseGoogleCodeEin<T = Record<string, unknown>>(endpunkt: string, code: string): Promise<GoogleEinloseErgebnis<T>> {
  const verifier = holeVerifier();
  if (!verifier) return { ok: false, art: 'anderes_fenster' };

  let antwort: Response;
  try {
    antwort = await fetch(endpunkt, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, code_verifier: verifier }),
    });
  } catch {
    return { ok: false, art: 'netz' };
  }

  try {
    sessionStorage.removeItem(SPEICHER_KEY);
  } catch {
    // egal
  }

  let daten: Record<string, unknown> = {};
  try {
    daten = await antwort.json();
  } catch {
    // leere/ungültige Antwort → unten als Fehler behandelt
  }
  if (antwort.ok) {
    return { ok: true, daten: daten as T };
  }
  const nachricht = typeof daten.message === 'string' ? daten.message : typeof daten.error === 'string' ? daten.error : undefined;
  if (antwort.status === 403) {
    return { ok: false, art: 'abgelehnt', nachricht };
  }
  if (antwort.status === 400) {
    return { ok: false, art: 'ungueltig', nachricht };
  }
  // 5xx und alles andere: nicht „Code abgelaufen" behaupten, sondern allgemein „bitte erneut versuchen".
  return { ok: false, art: 'netz', nachricht };
}

/**
 * Anzeigetext zu einem Einlöse-Fehler (`t` = i18n-Funktion der Seite).
 * Nimmt das ganze Ergebnis: Das Web-Projekt läuft ohne `strictNullChecks`, dort verengt `if (!ergebnis.ok)` die Union nicht —
 * ein Parameter nur für den Fehlerzweig ließe sich an der Aufrufstelle nicht übergeben (TS2345).
 */
export function googleEinloeseFehlerText(ergebnis: GoogleEinloseErgebnis<unknown>, t: (key: string) => string): string {
  const fehler = ergebnis as { art?: GoogleEinloeseFehler; nachricht?: string };
  switch (fehler.art) {
    case 'anderes_fenster':
      return t('auth.googleAnderesFenster');
    case 'abgelehnt':
      // Einzelsitzungs-Durchsetzung u. ä.: der Server nennt den Grund selbst.
      return fehler.nachricht || t('auth.googleBitteErneut');
    case 'netz':
      return t('auth.googleBitteErneut');
    default:
      return t('auth.googleCodeUngueltig');
  }
}
